import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import PerformanceReviewedSessionManagement from "./PerformanceReviewedSessionManagement";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

// ─── Constants ───────────────────────────────────────────────────────────────
const API_BASE = "https://fairpattern-secure-server-413055756357.europe-west1.run.app"
// const API_BASE = "http://localhost:8080"

const METRICS = [
  {
    key: "ui_understanding_score",
    avgKey: "avg_ui_understanding",
    label: "UI Understanding",
    color: "#6366f1",
    icon: "🖥️",
    plain: "Did the model correctly understand what the UI screen is about — its purpose, key elements, and overall context?",
  },
  {
    key: "concept_alignment_score",
    avgKey: "avg_concept_alignment",
    label: "Concept Alignment",
    color: "#10b981",
    icon: "🎯",
    plain: "How well did the model's identified dark-pattern concepts overlap with the human auditor's concepts? Scored on conceptual meaning, not exact wording.",
  },
  {
    key: "issue_accuracy_score",
    avgKey: "avg_issue_accuracy",
    label: "Issue Accuracy",
    color: "#f59e0b",
    icon: "✅",
    plain: "Of the concerns raised by the model, how many are genuinely valid according to the human auditor? High = few false alarms.",
  },
  {
    key: "issue_completeness_score",
    avgKey: "avg_issue_completeness",
    label: "Issue Completeness",
    color: "#ef4444",
    icon: "🔍",
    plain: "How much of the human auditor's assessment did the model manage to capture? High = few missed issues.",
  },
  {
    key: "issue_quality_score",
    avgKey: "avg_issue_quality",
    label: "Issue Quality",
    color: "#f97316",
    icon: "⚖️",
    plain: "Balanced measure: (Issue Accuracy + Issue Completeness) / 2. Reflects overall issue-level performance.",
  },
  {
    key: "composite_score",
    avgKey: "avg_composite_score",
    label: "Composite Score",
    color: "#8b5cf6",
    icon: "🌟",
    plain: "Headline KPI: 25% UI Understanding + 30% Concept Alignment + 20% Issue Accuracy + 25% Issue Completeness.",
  },
];

const OVERALL_METRICS = METRICS.filter(({ key }) =>
  ["ui_understanding_score", "concept_alignment_score", "issue_accuracy_score"].includes(key)
);

const CHART_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

// ─── Helpers ─────────────────────────────────────────────────────────────────
async function apiFetch(path, options) {
  const res = await fetch(`${API_BASE}/admin-tool/v1${path}`, options);
  if (!res.ok) throw new Error(`API error ${res.status}: ${path}`);
  return res.json();
}

function fmt(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(1)}`;
}

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(1)}%`;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color = "#6366f1", icon, tooltip }) {
  return (
    <div style={{
      background: "white",
      borderRadius: 12,
      padding: "16px 20px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)",
      borderLeft: `4px solid ${color}`,
      flex: 1,
      minWidth: 140,
    }}>
      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 4, display: "flex", alignItems: "center", gap: "4px" }}>
        {icon && <span>{icon}</span>}
        <span>{label}</span>
        {tooltip && (
          <span title={tooltip} style={{ cursor: "help", background: "#f3f4f6", borderRadius: "50%", width: "14px", height: "14px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "#6b7280" }}>
            i
          </span>
        )}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: "#111827", lineHeight: 1 }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function MetricBadge({ label, value, color, tooltip }) {
  return (
    <div style={{
      background: `${color}18`,
      border: `1px solid ${color}40`,
      borderRadius: 10,
      padding: "10px 16px",
      minWidth: 110,
      textAlign: "center",
      position: "relative",
    }}>
      <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", justifyContent: "center", alignItems: "center", gap: "4px" }}>
        {label}
        {tooltip && (
          <span title={tooltip} style={{ cursor: "help", background: "#e5e7eb", borderRadius: "50%", width: "14px", height: "14px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "#4b5563" }}>
            i
          </span>
        )}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function ScoreBar({ label, value, color, max = 100 }) {
  const pct = Math.min(100, Number(value) || 0);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: "#4b5563", fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 800, color }}>{fmt(value)}/100</span>
      </div>
      <div style={{ height: 8, background: "#f3f4f6", borderRadius: 8 }}>
        <div style={{ width: `${pct}%`, height: 8, borderRadius: 8, background: color, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

function ConceptChips({ items, color }) {
  if (!items || items.length === 0) return <span style={{ fontSize: 12, color: "#9ca3af" }}>—</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {items.map((c, i) => (
        <span key={i} style={{
          background: `${color}15`,
          border: `1px solid ${color}40`,
          color,
          borderRadius: 20,
          padding: "2px 10px",
          fontSize: 11,
          fontWeight: 600,
        }}>{c}</span>
      ))}
    </div>
  );
}

function ImageTable({ images }) {
  if (!images || images.length === 0) return <div style={{ padding: "24px", color: "#9ca3af", textAlign: "center" }}>No images found</div>;
  const cols = [
    { label: "Image", key: "img" },
    { label: "Page", key: "page_name" },
    { label: "UI Understanding", key: "ui_understanding_score", color: "#6366f1" },
    { label: "Concept Alignment", key: "concept_alignment_score", color: "#10b981" },
    { label: "Issue Accuracy", key: "issue_accuracy_score", color: "#f59e0b" },
    { label: "Issue Completeness", key: "issue_completeness_score", color: "#ef4444" },
    { label: "Issue Quality", key: "issue_quality_score", color: "#f97316" },
    { label: "Composite", key: "composite_score", color: "#8b5cf6" },
  ];
  return (
    <div style={{ overflowX: "auto", borderRadius: 10, border: "1px solid #e5e7eb", marginTop: 12 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#f9fafb" }}>
            {cols.map(c => (
              <th key={c.key} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: c.color || "#6b7280", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {images.map((img, i) => (
            <tr key={i} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? "white" : "#fafafa" }}>
              <td style={{ padding: "8px 12px" }}>
                {img.image_url ? (
                  <a href={img.image_url} target="_blank" rel="noreferrer" style={{ color: "#6366f1", textDecoration: "none", fontSize: 11 }}>View →</a>
                ) : "—"}
              </td>
              <td style={{ padding: "8px 12px", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{img.page_name || "—"}</td>
              <td style={{ padding: "8px 12px", fontWeight: 700, color: "#6366f1" }}>{fmt(img.ui_understanding_score)}</td>
              <td style={{ padding: "8px 12px", fontWeight: 700, color: "#10b981" }}>{fmt(img.concept_alignment_score)}</td>
              <td style={{ padding: "8px 12px", fontWeight: 700, color: "#f59e0b" }}>{fmt(img.issue_accuracy_score)}</td>
              <td style={{ padding: "8px 12px", fontWeight: 700, color: "#ef4444" }}>{fmt(img.issue_completeness_score)}</td>
              <td style={{ padding: "8px 12px", fontWeight: 700, color: "#f97316" }}>{fmt(img.issue_quality_score)}</td>
              <td style={{ padding: "8px 12px", fontWeight: 700, color: "#8b5cf6" }}>{fmt(img.composite_score)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RadarChart({ models, selectedModels }) {
  const metricKeys = METRICS.map(m => m.avgKey);
  const labels = METRICS.map(m => m.label);
  const cx = 160, cy = 160, r = 110;
  const n = metricKeys.length;

  const getPoint = (angle, val, maxVal = 100) => {
    const rad = (angle * Math.PI) / 180;
    const dist = (val / maxVal) * r;
    return { x: cx + dist * Math.sin(rad), y: cy - dist * Math.cos(rad) };
  };

  const axes = metricKeys.map((_, i) => {
    const angle = (360 / n) * i;
    const end = getPoint(angle, 100);
    return { angle, end, label: labels[i] };
  });

  const filteredModels = models.filter(m => selectedModels.includes(m.model));

  return (
    <svg viewBox="0 0 320 320" style={{ width: "100%", maxWidth: 320, margin: "0 auto", display: "block" }}>
      {[20, 40, 60, 80, 100].map(v => {
        const pts = axes.map(ax => {
          const p = getPoint(ax.angle, v);
          return `${p.x},${p.y}`;
        }).join(" ");
        return <polygon key={v} points={pts} fill="none" stroke="#e5e7eb" strokeWidth={v === 100 ? 1.5 : 1} />;
      })}
      {axes.map((ax, i) => (
        <g key={i}>
          <line x1={cx} y1={cy} x2={ax.end.x} y2={ax.end.y} stroke="#d1d5db" strokeWidth={1} />
          <text
            x={ax.end.x + (ax.end.x - cx) * 0.2}
            y={ax.end.y + (ax.end.y - cy) * 0.2}
            textAnchor="middle" dominantBaseline="middle"
            style={{ fontSize: 9, fill: "#6b7280", fontWeight: 600 }}
          >
            {ax.label}
          </text>
        </g>
      ))}
      {filteredModels.map((m, mi) => {
        const pts = METRICS.map((metric, i) => {
          const angle = (360 / n) * i;
          const val = Number(m[metric.avgKey] ?? m[metric.key]) || 0;
          const p = getPoint(angle, val);
          return `${p.x},${p.y}`;
        }).join(" ");
        const color = CHART_COLORS[mi % CHART_COLORS.length];
        return (
          <polygon key={m.model} points={pts} fill={color} fillOpacity={0.15} stroke={color} strokeWidth={2} />
        );
      })}
      <circle cx={cx} cy={cy} r={3} fill="#9ca3af" />
    </svg>
  );
}

// ─── Animated Score Result Card ───────────────────────────────────────────────
function AnalysisResultPanel({ result, onReset }) {
  const [animValues, setAnimValues] = useState({
    ui_understanding_score: 0,
    concept_alignment_score: 0,
    issue_accuracy_score: 0,
    issue_completeness_score: 0,
    issue_quality_score: 0,
    composite_score: 0,
  });

  useEffect(() => {
    if (!result) return;
    const duration = 800;
    const steps = 40;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      const progress = step / steps;
      setAnimValues({
        ui_understanding_score: Math.round((result.scores.ui_understanding_score || 0) * progress),
        concept_alignment_score: Math.round((result.scores.concept_alignment_score || 0) * progress),
        issue_accuracy_score: Math.round((result.scores.issue_accuracy_score || 0) * progress),
        issue_completeness_score: Math.round((result.scores.issue_completeness_score || 0) * progress),
        issue_quality_score: Math.round((result.scores.issue_quality_score || 0) * progress),
        composite_score: Math.round((result.scores.composite_score || 0) * progress),
      });
      if (step >= steps) clearInterval(interval);
    }, duration / steps);
    return () => clearInterval(interval);
  }, [result]);

  if (!result) return null;

  return (
    <div style={{ marginTop: 32 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: "#111827", margin: 0 }}>
          ✨ Analysis Results
          {result.saved_id && (
            <span style={{ marginLeft: 10, fontSize: 11, background: "#d1fae5", color: "#065f46", padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>
              Saved to DB ✓
            </span>
          )}
        </h3>
        <button
          onClick={onReset}
          style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12, color: "#6b7280", fontWeight: 600 }}
        >
          New Analysis
        </button>
      </div>

      {/* Composite score big display */}
      <div style={{
        background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
        borderRadius: 16, padding: "24px 32px", marginBottom: 24,
        display: "flex", alignItems: "center", gap: 24,
      }}>
        <div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600, marginBottom: 4 }}>COMPOSITE SCORE</div>
          <div style={{ fontSize: 56, fontWeight: 900, color: "white", lineHeight: 1 }}>
            {animValues.composite_score}
            <span style={{ fontSize: 20, fontWeight: 400, color: "rgba(255,255,255,0.6)" }}>/100</span>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            {result.explanation}
          </p>
        </div>
      </div>

      {/* 5 metric score bars */}
      <div style={{ background: "white", borderRadius: 14, padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)", marginBottom: 20 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginTop: 0, marginBottom: 20 }}>Detailed Scores</h4>
        {METRICS.filter(m => m.key !== "composite_score").map(m => (
          <ScoreBar key={m.key} label={`${m.icon} ${m.label}`} value={animValues[m.key]} color={m.color} />
        ))}
      </div>

      {/* Concepts panel */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
        {[
          { title: "🤖 Model Concepts", items: result.model_concepts, color: "#6366f1" },
          { title: "👤 Human Concepts", items: result.human_concepts, color: "#10b981" },
          { title: "🔗 Matched Concepts", items: result.matched_concepts, color: "#8b5cf6" },
        ].map(({ title, items, color }) => (
          <div key={title} style={{ background: "white", borderRadius: 12, padding: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 10 }}>{title}</div>
            <ConceptChips items={items} color={color} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tab C: Image Analyzer ────────────────────────────────────────────────────
function ImageAnalyzerTab() {
  const [form, setForm] = useState({
    initial_outcome: "",
    final_response: "",
    image_url: "",
    source_type: "standalone",
    session_name: "",
    model_name: "",
    run_label: "",
    page_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleAnalyze = async () => {
    if (!form.initial_outcome.trim() || !form.final_response.trim()) {
      setError("Both Initial Finding and Final Finding are required.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        initial_outcome: form.initial_outcome,
        final_response: form.final_response,
        image_url: form.image_url || null,
        source_type: form.source_type,
        session_name: form.source_type === "session" ? form.session_name : null,
        model_name: form.source_type === "model" ? form.model_name : null,
        run_label: form.source_type === "model" ? form.run_label : null,
        page_name: form.page_name || null,
      };
      const data = await apiFetch("/analyze-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#1f2937",
    background: "white",
    outline: "none",
    boxSizing: "border-box",
    resize: "vertical",
  };

  const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 6,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "#111827", margin: "0 0 6px 0" }}>
          🔬 Image Analysis
        </h2>
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
          Paste the initial model finding and the final auditor response. The LLM will evaluate concept-level alignment and store the result.
        </p>
      </div>

      {result ? (
        <AnalysisResultPanel result={result} onReset={() => { setResult(null); setForm(prev => ({ ...prev, initial_outcome: "", final_response: "" })); }} />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* Left: Text inputs */}
          <div>
            <div style={{ background: "white", borderRadius: 14, padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)", marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginTop: 0, marginBottom: 16 }}>📝 Findings</h3>

              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Initial Finding <span style={{ color: "#ef4444" }}>*</span></label>
                <textarea
                  rows={6}
                  placeholder="Paste the model's initial outcome / system response here…"
                  value={form.initial_outcome}
                  onChange={e => handleChange("initial_outcome", e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Final Finding (Auditor) <span style={{ color: "#ef4444" }}>*</span></label>
                <textarea
                  rows={6}
                  placeholder="Paste the human auditor's final response / dark pattern findings here…"
                  value={form.final_response}
                  onChange={e => handleChange("final_response", e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>

          {/* Right: Metadata */}
          <div>
            <div style={{ background: "white", borderRadius: 14, padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)", marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginTop: 0, marginBottom: 16 }}>🏷️ Metadata</h3>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Image URL (optional)</label>
                <input
                  type="url"
                  placeholder="https://…"
                  value={form.image_url}
                  onChange={e => handleChange("image_url", e.target.value)}
                  style={{ ...inputStyle, resize: "none" }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Page Name (optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Checkout Page"
                  value={form.page_name}
                  onChange={e => handleChange("page_name", e.target.value)}
                  style={{ ...inputStyle, resize: "none" }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Source Type</label>
                <select
                  value={form.source_type}
                  onChange={e => handleChange("source_type", e.target.value)}
                  style={{ ...inputStyle, resize: "none", cursor: "pointer" }}
                >
                  <option value="standalone">Standalone (ad-hoc)</option>
                  <option value="session">Session-based</option>
                  <option value="model">Model Evaluation</option>
                </select>
              </div>

              {form.source_type === "session" && (
                <div style={{ marginBottom: 14, padding: "14px", background: "#f0fdf4", borderRadius: 10, border: "1px solid #d1fae5" }}>
                  <label style={{ ...labelStyle, color: "#065f46" }}>Session Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Amazon Prime Checkout Q1"
                    value={form.session_name}
                    onChange={e => handleChange("session_name", e.target.value)}
                    style={{ ...inputStyle, resize: "none" }}
                  />
                </div>
              )}

              {form.source_type === "model" && (
                <div style={{ padding: "14px", background: "#eff6ff", borderRadius: 10, border: "1px solid #dbeafe" }}>
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ ...labelStyle, color: "#1e40af" }}>Model Name</label>
                    <input
                      type="text"
                      placeholder="e.g. gpt_4o_mini"
                      value={form.model_name}
                      onChange={e => handleChange("model_name", e.target.value)}
                      style={{ ...inputStyle, resize: "none" }}
                    />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, color: "#1e40af" }}>Run Label (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. v2-batch1"
                      value={form.run_label}
                      onChange={e => handleChange("run_label", e.target.value)}
                      style={{ ...inputStyle, resize: "none" }}
                    />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#dc2626", fontSize: 13 }}>
                ⚠️ {error}
              </div>
            )}

            <button
              onClick={handleAnalyze}
              disabled={loading || !form.initial_outcome.trim() || !form.final_response.trim()}
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: 12,
                border: "none",
                background: loading ? "#a5b4fc" : "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                color: "white",
                fontSize: 15,
                fontWeight: 800,
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: "0 4px 12px rgba(79,70,229,0.3)",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              {loading ? (
                <>
                  <div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.4)", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  Analyzing with LLM…
                </>
              ) : (
                "🔬 Analyze Findings"
              )}
            </button>

            <p style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", marginTop: 10 }}>
              Results are automatically saved to the database
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Tab D: Agent Prompts Configuration ───────────────────────────────────────
function AgentPromptsTab() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(null); // stores the prompt object being edited
  const [editContent, setEditContent] = useState("");
  const [whatUpdated, setWhatUpdated] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  const fetchPrompts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch("/prompts");
      setPrompts(data.prompts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const handleEdit = (p) => {
    setEditing(p);
    setEditContent(p.prompt_content);
    setWhatUpdated("");
  };

  const handleCancel = () => {
    setEditing(null);
    setEditContent("");
    setWhatUpdated("");
  };

  const handleSave = async () => {
    if (!whatUpdated.trim()) {
      alert("Please provide a summary of what you updated.");
      return;
    }
    setSaveLoading(true);
    try {
      await apiFetch("/prompts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent_name: editing.agent_name,
          prompt_type: editing.prompt_type,
          prompt_content: editContent,
          what_updated: whatUpdated,
        }),
      });
      setEditing(null);
      await fetchPrompts();
    } catch (e) {
      alert("Error saving prompt: " + e.message);
    } finally {
      setSaveLoading(false);
    }
  };

  if (loading && prompts.length === 0) {
    return <div>Loading prompts...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>Error: {error}</div>;
  }

  // Group by agent name
  const grouped = prompts.reduce((acc, p) => {
    if (!acc[p.agent_name]) acc[p.agent_name] = [];
    acc[p.agent_name].push(p);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "#111827", margin: "0 0 6px 0" }}>
          ⚙️ Agent Prompts Configuration
        </h2>
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
          Manage the system and human prompts for all pipeline agents.
        </p>
      </div>

      {Object.keys(grouped).sort().map(agentName => (
        <div key={agentName} style={{ background: "white", borderRadius: 14, padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)", marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: "#4f46e5", marginTop: 0, marginBottom: 16 }}>{agentName}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            {grouped[agentName].sort((a, b) => a.prompt_type.localeCompare(b.prompt_type)).map(p => (
              <div key={p.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "#374151" }}>{p.prompt_type} Prompt</span>
                  <span style={{ fontSize: 11, color: "#6b7280", background: "#f3f4f6", padding: "2px 8px", borderRadius: 12 }}>v{p.version}</span>
                </div>

                {editing && editing.id === p.id ? (
                  <div>
                    <textarea
                      style={{ width: "100%", minHeight: 200, padding: 10, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, marginBottom: 12 }}
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="What updated? (Required)"
                      style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 13, marginBottom: 12 }}
                      value={whatUpdated}
                      onChange={e => setWhatUpdated(e.target.value)}
                    />
                    <div style={{ display: "flex", gap: 10 }}>
                      <button onClick={handleSave} disabled={saveLoading} style={{ background: "#10b981", color: "white", padding: "6px 12px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                        {saveLoading ? "Saving..." : "Save"}
                      </button>
                      <button onClick={handleCancel} disabled={saveLoading} style={{ background: "#f3f4f6", color: "#374151", padding: "6px 12px", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 12, color: "#4b5563", background: "#f9fafb", padding: 12, borderRadius: 8, whiteSpace: "pre-wrap", maxHeight: 200, overflowY: "auto", marginBottom: 12, border: "1px solid #f3f4f6" }}>
                      {p.prompt_content}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        Last updated: {new Date(p.updated_at).toLocaleDateString()}
                        <br />
                        Note: {p.what_updated || "N/A"}
                      </div>
                      <button onClick={() => handleEdit(p)} style={{ background: "white", border: "1px solid #d1d5db", color: "#374151", padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


// ─── Main Dashboard Component ─────────────────────────────────────────────────
export default function Dashboard() {
  // ── Global state
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Metrics toggle: 'overall' | 'latest'
  const [metricsView, setMetricsView] = useState("latest");

  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [expandedJourney, setExpandedJourney] = useState(null);
  const [journeyImages, setJourneyImages] = useState({});
  const [journeyImagesLoading, setJourneyImagesLoading] = useState({});
  const [subTabA, setSubTabA] = useState("view");

  // ── Section B state
  const [selectedModels, setSelectedModels] = useState([]);
  const [activeTab, setActiveTab] = useState("A");
  const [modelImages, setModelImages] = useState({});
  const [modelImagesLoading, setModelImagesLoading] = useState({});

  // ── Load overview + sessions + models
  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiFetch("/dashboard/overview"),
      apiFetch("/dashboard/sessions"),
      apiFetch("/dashboard/models"),
    ])
      .then(([ov, sess, mods]) => {
        setOverview(ov);
        const fetchedSessions = sess.sessions || [];
        setSessions(fetchedSessions);
        if (fetchedSessions.length > 0) {
          setSelectedSession(fetchedSessions[0].session_id);
        }
        setModels(mods.models || []);
        if (mods.models && mods.models.length > 0) {
          setSelectedModels([mods.models[0].model]);
        }
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const loadModelImages = useCallback(async (modelName) => {
    if (modelImages[modelName]) return;
    setModelImagesLoading(prev => ({ ...prev, [modelName]: true }));
    try {
      const d = await apiFetch(`/dashboard/model/${modelName}/images`);
      setModelImages(prev => ({ ...prev, [modelName]: d.images || [] }));
    } catch {
      setModelImages(prev => ({ ...prev, [modelName]: [] }));
    }
    setModelImagesLoading(prev => ({ ...prev, [modelName]: false }));
  }, [modelImages]);

  useEffect(() => {
    selectedModels.forEach(m => loadModelImages(m));
  }, [selectedModels, loadModelImages]);

  // ── Load session detail
  useEffect(() => {
    if (!selectedSession) return;
    setSessionLoading(true);
    setSessionData(null);
    setExpandedJourney(null);
    apiFetch(`/dashboard/session/${selectedSession}`)
      .then(d => { setSessionData(d); setSessionLoading(false); })
      .catch(e => { setError(e.message); setSessionLoading(false); });
  }, [selectedSession]);

  // ── Load journey images
  const loadJourneyImages = useCallback(async (journeyId) => {
    if (journeyImages[journeyId]) return;
    setJourneyImagesLoading(prev => ({ ...prev, [journeyId]: true }));
    try {
      const d = await apiFetch(`/dashboard/session/${selectedSession}/journey/${journeyId}/images`);
      setJourneyImages(prev => ({ ...prev, [journeyId]: d.images || [] }));
    } catch {
      setJourneyImages(prev => ({ ...prev, [journeyId]: [] }));
    }
    setJourneyImagesLoading(prev => ({ ...prev, [journeyId]: false }));
  }, [selectedSession, journeyImages]);

  const handleJourneyClick = (journeyId) => {
    if (expandedJourney === journeyId) {
      setExpandedJourney(null);
    } else {
      setExpandedJourney(journeyId);
      loadJourneyImages(journeyId);
    }
  };

  const toggleModel = (m) => {
    setSelectedModels(prev =>
      prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]
    );
  };

  // ── Loading / error state
  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh", background: "#f9fafb" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, border: "4px solid #e5e7eb", borderTop: "4px solid #6366f1", borderRadius: "50%", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
          <p style={{ color: "#6b7280", fontSize: 16 }}>Loading performance data…</p>
          <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 24 }}>
          <h2 style={{ color: "#991b1b", marginBottom: 8 }}>Error Loading Dashboard</h2>
          <p style={{ color: "#dc2626" }}>{error}</p>
        </div>
      </div>
    );
  }

  const journeyMetrics = sessionData?.journey_metrics || [];
  const overallMetrics = overview?.overall_metrics || {};

  const TABS = [
    { id: "A", label: "📊 System Performance" },
    { id: "B", label: "🤖 Model Comparison" },
    { id: "C", label: "🔬 Analyze Image" },
    { id: "D", label: "⚙️ Agent Configuration" },
  ];

  return (
    <div style={{ padding: "28px 32px", background: "#f9fafb", minHeight: "100vh", fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: "#111827", margin: "0 0 4px 0" }}>
          🔬 Performance Dashboard
        </h1>
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
          Concept-level evaluation of UI dark-pattern auditing quality
        </p>
      </div>

      {/* ── Top-level KPIs ── */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        <KpiCard label="Sessions Reviewed" value={overview?.total_sessions} color="#6366f1" icon="📋" />
        <KpiCard label="User Journeys" value={overview?.total_user_journeys} color="#10b981" icon="🗺️" />
        <KpiCard label="Images Evaluated" value={overview?.total_images} color="#f59e0b" icon="🖼️" />
        <KpiCard label="Models Benchmarked" value={overview?.models_evaluated?.length} sub={overview?.models_evaluated?.join(", ")} color="#8b5cf6" icon="🤖" />
      </div>

      {/* ── Section Tabs ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: 28, background: "#f3f4f6", padding: 4, borderRadius: 12, width: "fit-content" }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: "8px 24px", borderRadius: 9, border: "none", cursor: "pointer",
              fontWeight: 700, fontSize: 13,
              background: activeTab === t.id ? "white" : "transparent",
              color: activeTab === t.id ? "#6366f1" : "#6b7280",
              boxShadow: activeTab === t.id ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
              transition: "all 0.15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════
          SECTION A — System Performance
          ════════════════════════════════════ */}
      {activeTab === "A" && (
        <div>
          {/* ── Concept-Level Metrics card with toggle ── */}
          {overview && (
            (overview.overall_metrics && Object.values(overview.overall_metrics).some(v => v !== null)) ||
            (overview.latest_trends && Object.values(overview.latest_trends).some(v => v !== null))
          ) && (() => {
            const activeMetrics = metricsView === "latest"
              ? (overview.latest_trends || {})
              : (overview.overall_metrics || {});
            const sessionLabel = metricsView === "latest"
              ? `Latest ${overview.latest_trends?.session_count ?? 5} Sessions`
              : `All ${overview.total_sessions ?? ""} Sessions`;
            return (
              <div style={{ background: "white", borderRadius: 14, padding: "20px 24px", marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
                {/* Card header + toggle */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: 0 }}>
                      📈 System Metrics
                    </h3>
                    <p style={{ fontSize: 11, color: "#9ca3af", margin: "3px 0 0 0" }}>
                      Avg. across {sessionLabel}
                    </p>
                  </div>
                  {/* Toggle pill */}
                  <div style={{
                    display: "flex", background: "#f3f4f6", borderRadius: 10,
                    padding: 3, gap: 2,
                  }}>
                    <button
                      onClick={() => setMetricsView("latest")}
                      style={{
                        padding: "5px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, transition: "all 0.15s",
                        background: metricsView === "latest" ? "white" : "transparent",
                        color: metricsView === "latest" ? "#6366f1" : "#9ca3af",
                        boxShadow: metricsView === "latest" ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                      }}
                    >
                      ⚡ Latest Trends
                    </button>
                    <button
                      onClick={() => setMetricsView("overall")}
                      style={{
                        padding: "5px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, transition: "all 0.15s",
                        background: metricsView === "overall" ? "white" : "transparent",
                        color: metricsView === "overall" ? "#374151" : "#9ca3af",
                        boxShadow: metricsView === "overall" ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                      }}
                    >
                      🌐 Overall Avg
                    </button>
                  </div>
                </div>

                {/* Metric badges — show only top 3 summary metrics */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 12, width: "100%",
                }}>
                  {METRICS.filter(m => !["issue_completeness_score", "issue_quality_score", "composite_score"].includes(m.key)).map(m => (
                    <MetricBadge
                      key={m.key}
                      label={`${m.icon} ${m.label}`}
                      tooltip={m.plain}
                      value={(activeMetrics[m.avgKey] ?? activeMetrics[m.key]) != null
                        ? `${fmt(activeMetrics[m.avgKey] ?? activeMetrics[m.key])}`
                        : "—"}
                      color={m.color}
                    />
                  ))}
                </div>



              </div>
            );
          })()}

          {/* ── Overall System Metrics Over Time ── */}
          {sessions.length > 0 && (
            <div style={{ background: "white", borderRadius: 14, padding: "20px 24px", marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginTop: 0, marginBottom: 16 }}>
                📈 Overall System Metrics Over Time
              </h3>
              <div style={{ height: "300px", width: "100%" }}>
                <Line
                  data={{
                    labels: [...sessions].sort((a, b) => new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at)).map(s => new Date(s.created_at || s.updated_at).toLocaleDateString() + ' ' + (s.session_name || s.session_id.substring(0, 6))),
                    datasets: [
                      { label: 'UI Understanding', data: [...sessions].sort((a, b) => new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at)).map(s => parseFloat(s.avg_ui_understanding) || 0), borderColor: '#6366f1', backgroundColor: '#6366f1', tension: 0.3 },
                      { label: 'Concept Alignment', data: [...sessions].sort((a, b) => new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at)).map(s => parseFloat(s.avg_concept_alignment) || 0), borderColor: '#10b981', backgroundColor: '#10b981', tension: 0.3 },
                      { label: 'Issue Accuracy', data: [...sessions].sort((a, b) => new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at)).map(s => parseFloat(s.avg_issue_accuracy) || 0), borderColor: '#f59e0b', backgroundColor: '#f59e0b', tension: 0.3 },
                      { label: 'Issue Completeness', data: [...sessions].sort((a, b) => new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at)).map(s => parseFloat(s.avg_issue_completeness) || 0), borderColor: '#ef4444', backgroundColor: '#ef4444', tension: 0.3 },
                      { label: 'Issue Quality', data: [...sessions].sort((a, b) => new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at)).map(s => parseFloat(s.avg_issue_quality) || 0), borderColor: '#f97316', backgroundColor: '#f97316', tension: 0.3 },
                      { label: 'Composite', data: [...sessions].sort((a, b) => new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at)).map(s => parseFloat(s.composite_score) || 0), borderColor: '#8b5cf6', backgroundColor: '#8b5cf6', tension: 0.3 },
                    ],
                  }}
                  options={{
                    responsive: true, maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    scales: { y: { beginAtZero: true, max: 100 } },
                    plugins: { legend: { position: 'top' } }
                  }}
                />
              </div>
            </div>
          )}

          {/* Sub-tabs for Section A */}
          <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: "1px solid #e5e7eb", paddingBottom: 12 }}>
            <button
              onClick={() => setSubTabA("view")}
              style={{
                padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                fontWeight: 600, fontSize: 13,
                background: subTabA === "view" ? "#eef2ff" : "transparent",
                color: subTabA === "view" ? "#4f46e5" : "#6b7280",
                transition: "all 0.15s",
              }}
            >
              👁️ View Session
            </button>
            <button
              onClick={() => setSubTabA("manage")}
              style={{
                padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                fontWeight: 600, fontSize: 13,
                background: subTabA === "manage" ? "#eef2ff" : "transparent",
                color: subTabA === "manage" ? "#4f46e5" : "#6b7280",
                transition: "all 0.15s",
              }}
            >
              ⚙️ Session Management
            </button>
          </div>

          {subTabA === "manage" && (
            <div style={{ marginBottom: 32 }}>
              <PerformanceReviewedSessionManagement />
            </div>
          )}

          {subTabA === "view" && (
            <>
              {/* Session Selector */}
              <div style={{ marginBottom: 24, display: "flex", gap: "24px", alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 300 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
                    Select Session
                  </label>
                  <select
                    value={selectedSession || ""}
                    onChange={e => setSelectedSession(e.target.value || null)}
                    style={{
                      padding: "10px 16px", borderRadius: 10, border: "1px solid #d1d5db",
                      fontSize: 14, color: "#1f2937", background: "white",
                      width: "100%", cursor: "pointer", outline: "none",
                    }}
                  >
                    {sessions.map(s => (
                      <option key={s.session_id} value={s.session_id}>
                        {s.session_name || s.session_id}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Session loading spinner */}
              {sessionLoading && (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <div style={{ width: 36, height: 36, border: "3px solid #e5e7eb", borderTop: "3px solid #6366f1", borderRadius: "50%", margin: "0 auto 12px", animation: "spin 1s linear infinite" }} />
                  <p style={{ color: "#9ca3af" }}>Computing session metrics…</p>
                </div>
              )}

              {/* Session Detail */}
              {sessionData && !sessionLoading && (
                <>
                  {/* Session KPI row — 6 concept-level metrics */}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
                    {METRICS.map(m => (
                      <KpiCard
                        key={m.key}
                        icon={m.icon}
                        label={m.label}
                        tooltip={m.plain}
                        value={`${fmt(sessionData[m.avgKey] ?? sessionData[m.key])}`}
                        color={m.color}
                      />
                    ))}
                  </div>

                  <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
                    <MetricBadge label="Total Images" value={sessionData.total_images} color="#6366f1" />
                    <MetricBadge label="User Journeys" value={sessionData.total_user_journeys} color="#8b5cf6" />
                  </div>

                  {/* User Journey Interactive Chart */}
                  {journeyMetrics.length > 0 && (
                    <div style={{ background: "white", borderRadius: 14, padding: "20px 24px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
                      <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", marginTop: 0, marginBottom: 4 }}>
                        Performance by User Journey
                      </h3>
                      <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
                        Click any bar to expand image-level details for that journey.
                      </p>
                      <div style={{ height: "350px", width: "100%", marginTop: "20px" }}>
                        <Bar
                          data={{
                            labels: journeyMetrics.map(uj => String(uj.journey_name || uj.journey_id || "Unknown")),
                            datasets: [
                              { label: 'UI Understanding', data: journeyMetrics.map(uj => parseFloat(uj.avg_ui_understanding) || 0), backgroundColor: '#6366f1', borderRadius: 4 },
                              { label: 'Concept Alignment', data: journeyMetrics.map(uj => parseFloat(uj.avg_concept_alignment) || 0), backgroundColor: '#10b981', borderRadius: 4 },
                              { label: 'Issue Accuracy', data: journeyMetrics.map(uj => parseFloat(uj.avg_issue_accuracy) || 0), backgroundColor: '#f59e0b', borderRadius: 4 },
                              { label: 'Issue Completeness', data: journeyMetrics.map(uj => parseFloat(uj.avg_issue_completeness) || 0), backgroundColor: '#ef4444', borderRadius: 4 },
                              { label: 'Issue Quality', data: journeyMetrics.map(uj => parseFloat(uj.avg_issue_quality) || 0), backgroundColor: '#f97316', borderRadius: 4 },
                              { label: 'Composite', data: journeyMetrics.map(uj => parseFloat(uj.avg_composite_score) || 0), backgroundColor: '#8b5cf6', borderRadius: 4 },
                            ],
                          }}
                          options={{
                            responsive: true, maintainAspectRatio: false,
                            interaction: { mode: 'index', intersect: false },
                            scales: { y: { beginAtZero: true, max: 100 } },
                            plugins: {
                              legend: { position: 'top' },
                              tooltip: { callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.parsed.y}` } }
                            },
                            onClick: (event, elements) => {
                              if (elements.length > 0) {
                                const idx = elements[0].index;
                                const uj = journeyMetrics[idx];
                                if (uj) handleJourneyClick(uj.journey_id);
                              }
                            }
                          }}
                        />
                      </div>

                      {/* Expanded Image Table */}
                      {expandedJourney && (
                        <div style={{ marginTop: 24, borderTop: "1px solid #e5e7eb", paddingTop: 20 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                            <h4 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "#374151" }}>
                              Images for: {journeyMetrics.find(j => j.journey_id === expandedJourney)?.journey_name || expandedJourney}
                            </h4>
                            <button
                              onClick={() => setExpandedJourney(null)}
                              style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                            >
                              Close Table ✕
                            </button>
                          </div>

                          {journeyImagesLoading[expandedJourney] ? (
                            <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>
                              <div style={{ width: 24, height: 24, border: "2px solid #e5e7eb", borderTop: "2px solid #6366f1", borderRadius: "50%", margin: "0 auto 8px", animation: "spin 1s linear infinite" }} />
                              Loading images…
                            </div>
                          ) : (
                            <>
                              <div style={{ height: "280px", width: "100%", marginBottom: "20px" }}>
                                <Bar
                                  data={{
                                    labels: (journeyImages[expandedJourney] || []).map((img, idx) => String(img.page_name || `Image ${idx + 1}`)),
                                    datasets: [
                                      { label: 'UI Understanding', data: (journeyImages[expandedJourney] || []).map(img => parseFloat(img.ui_understanding_score) || 0), backgroundColor: '#6366f1', borderRadius: 4 },
                                      { label: 'Concept Alignment', data: (journeyImages[expandedJourney] || []).map(img => parseFloat(img.concept_alignment_score) || 0), backgroundColor: '#10b981', borderRadius: 4 },
                                      { label: 'Issue Quality', data: (journeyImages[expandedJourney] || []).map(img => parseFloat(img.issue_quality_score) || 0), backgroundColor: '#f59e0b', borderRadius: 4 },
                                      { label: 'Composite', data: (journeyImages[expandedJourney] || []).map(img => parseFloat(img.composite_score) || 0), backgroundColor: '#8b5cf6', borderRadius: 4 },
                                    ],
                                  }}
                                  options={{
                                    responsive: true, maintainAspectRatio: false,
                                    interaction: { mode: 'index', intersect: false },
                                    scales: { y: { beginAtZero: true, max: 100 } },
                                    plugins: { legend: { position: 'top' } }
                                  }}
                                />
                              </div>
                              <ImageTable images={journeyImages[expandedJourney]} />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {journeyMetrics.length === 0 && (
                    <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                      No user journey data found for this session.
                    </div>
                  )}
                </>
              )}

              {!selectedSession && !sessionLoading && (
                <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
                  <p style={{ fontSize: 14 }}>Select a session above to view its performance metrics</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════
          SECTION B — Model Comparison
          ════════════════════════════════════ */}
      {activeTab === "B" && (
        <div>
          {models.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
              <p>No model evaluation runs found. Run the model evaluator to see data here.</p>
            </div>
          ) : (
            <>
              {/* Model selector */}
              <div style={{ background: "white", borderRadius: 14, padding: "20px 24px", marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginTop: 0, marginBottom: 12 }}>Select Models to Compare</h3>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {models.map((m, i) => {
                    const c = CHART_COLORS[i % CHART_COLORS.length];
                    const active = selectedModels.includes(m.model);
                    return (
                      <button
                        key={m.model}
                        onClick={() => toggleModel(m.model)}
                        style={{
                          padding: "8px 18px", borderRadius: 20,
                          border: `2px solid ${active ? c : "#e5e7eb"}`,
                          background: active ? `${c}15` : "white",
                          color: active ? c : "#6b7280",
                          fontWeight: 700, fontSize: 12, cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {m.model.replace(/_/g, " ")}
                        {m.version && <span style={{ marginLeft: 4, opacity: 0.7 }}>v{m.version}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
                {/* Radar Chart */}
                <div style={{ background: "white", borderRadius: 14, padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", marginTop: 0, marginBottom: 4 }}>Model Comparison Radar</h3>
                  <p style={{ fontSize: 11, color: "#9ca3af", marginBottom: 16 }}>Larger area = better overall performance</p>
                  <RadarChart models={models} selectedModels={selectedModels} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16, justifyContent: "center" }}>
                    {models.filter(m => selectedModels.includes(m.model)).map((m, i) => {
                      const c = CHART_COLORS[i % CHART_COLORS.length];
                      return (
                        <div key={m.model} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280" }}>
                          <div style={{ width: 12, height: 12, borderRadius: 2, background: c }} />
                          {m.model.replace(/_/g, " ")} {m.version && `v${m.version}`}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Metrics table */}
                <div style={{ background: "white", borderRadius: 14, padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.07)", overflowX: "auto" }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", marginTop: 0, marginBottom: 16 }}>Metrics Table</h3>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#f9fafb" }}>
                        <th style={{ padding: "8px 10px", textAlign: "left", color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>Model</th>
                        {METRICS.map(m => (
                          <th key={m.key} style={{ padding: "8px 10px", textAlign: "right", color: m.color, fontWeight: 700, borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>
                            {m.icon} {m.label}
                          </th>
                        ))}
                        <th style={{ padding: "8px 10px", textAlign: "right", color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>Images</th>
                        <th style={{ padding: "8px 10px", textAlign: "left", color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb" }}>Version / Run</th>
                      </tr>
                    </thead>
                    <tbody>
                      {models.filter(m => selectedModels.includes(m.model)).map((m, i) => (
                        <tr key={m.model} style={{ borderBottom: "1px solid #f3f4f6", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                          <td style={{ padding: "8px 10px", fontWeight: 700, color: "#1f2937" }}>{m.model.replace(/_/g, " ")}</td>
                          {METRICS.map(met => (
                            <td key={met.key} style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: met.color }}>
                              {fmt(m[met.avgKey] ?? m[met.key])}
                            </td>
                          ))}
                          <td style={{ padding: "8px 10px", textAlign: "right", color: "#6b7280" }}>{m.total_images}</td>
                          <td style={{ padding: "8px 10px", color: "#9ca3af", fontSize: 10 }}>
                            {m.version && <span style={{ fontWeight: 700, color: "#6366f1" }}>v{m.version} </span>}
                            {m.run_label || new Date(m.run_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Image-level line charts and tables for selected models */}
              {selectedModels.map(modelName => {
                const images = modelImages[modelName] || [];
                const isLoading = modelImagesLoading[modelName];

                return (
                  <div key={`details-${modelName}`} style={{ background: "white", borderRadius: 14, padding: "24px", marginBottom: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
                    <h3 style={{ fontSize: 16, fontWeight: 800, color: "#1f2937", marginTop: 0, marginBottom: 16 }}>
                      Image-Level Breakdown: {modelName.replace(/_/g, " ")}
                    </h3>

                    {isLoading ? (
                      <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>
                        <div style={{ width: 24, height: 24, border: "2px solid #e5e7eb", borderTop: "2px solid #6366f1", borderRadius: "50%", margin: "0 auto 8px", animation: "spin 1s linear infinite" }} />
                        Loading images…
                      </div>
                    ) : images.length === 0 ? (
                      <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af" }}>
                        No image-level data found for this model run. Run a new evaluation to generate this data.
                      </div>
                    ) : (
                      <>
                        <div style={{ height: "300px", width: "100%", marginBottom: "24px" }}>
                          <Line
                            data={{
                              labels: images.map((img, idx) => img.page_name || `Image ${idx + 1}`),
                              datasets: [
                                { label: 'UI Understanding', data: images.map(img => parseFloat(img.ui_understanding_score) || 0), borderColor: '#6366f1', backgroundColor: '#6366f1', tension: 0.3 },
                                { label: 'Concept Alignment', data: images.map(img => parseFloat(img.concept_alignment_score) || 0), borderColor: '#10b981', backgroundColor: '#10b981', tension: 0.3 },
                                { label: 'Issue Accuracy', data: images.map(img => parseFloat(img.issue_accuracy_score) || 0), borderColor: '#f59e0b', backgroundColor: '#f59e0b', tension: 0.3 },
                                { label: 'Issue Completeness', data: images.map(img => parseFloat(img.issue_completeness_score) || 0), borderColor: '#ef4444', backgroundColor: '#ef4444', tension: 0.3 },
                                { label: 'Issue Quality', data: images.map(img => parseFloat(img.issue_quality_score) || 0), borderColor: '#f97316', backgroundColor: '#f97316', tension: 0.3 },
                                { label: 'Composite', data: images.map(img => parseFloat(img.composite_score) || 0), borderColor: '#8b5cf6', backgroundColor: '#8b5cf6', tension: 0.3 },
                              ],
                            }}
                            options={{
                              responsive: true, maintainAspectRatio: false,
                              interaction: { mode: 'index', intersect: false },
                              scales: { y: { beginAtZero: true, max: 100 } },
                              plugins: { legend: { position: 'top' } }
                            }}
                          />
                        </div>
                        <h4 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 12px 0", color: "#374151" }}>All Evaluated Images</h4>
                        <ImageTable images={images} />
                      </>
                    )}
                  </div>
                );
              })}


            </>
          )}
        </div>
      )}

      {/* ════════════════════════════════════
          SECTION C — Image Analyzer
          ════════════════════════════════════ */}
      {activeTab === "C" && <ImageAnalyzerTab />}

      {/* ════════════════════════════════════
          SECTION D — Agent Prompts Configuration
          ════════════════════════════════════ */}
      {activeTab === "D" && <AgentPromptsTab />}
    </div>
  );
}
